import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Flex, Image, Badge, Link, Icon } from "@chakra-ui/react";
import { FileText } from "lucide-react";
import { useLang, useText } from "../../i18n/lang";

const DOCS = "/images/documente/premii/";
const LOGOS = "/images/logoStats/web/";

// Titlurile sunt cele din documente (trofee, certificate, scrisori de recomandare)
const AWARDS = [
  { airline: "APG", logo: "apg.webp", w: 188, year: "2024", type: "trophy", doc: "AGP-2024.jpg", title: { en: "GP-275 Sales Champion", ro: "GP-275 Sales Champion" } },
  { airline: "Turkish Airlines", logo: "turkish.webp", w: 212, year: "2023", type: "trophy", doc: "TK 2023.jpg", title: { en: "3rd place in total sales", ro: "Locul 3 în vânzări totale" } },
  { airline: "TAROM", logo: "tarom.webp", w: 198, year: "2024", type: "letter", doc: "Tarom Recomdantion Travel Time.pdf", title: { en: "One of TAROM's top IATA sales partners", ro: "Unul dintre partenerii IATA de top ai TAROM" } },
  { airline: "Lufthansa Group", logo: "lufthansa.webp", w: 347, year: "2025", type: "letter", doc: "Lufthansa.pdf", title: { en: "Recommended as a reliable and competent business partner", ro: "Recomandați ca partener de afaceri de încredere" } },
  { airline: "Turkish Airlines", logo: "turkish.webp", w: 212, year: "2023", type: "letter", doc: "Turkish Airlines Recomdantion Travel Time.pdf", title: { en: "Valued partner with a long collaboration history", ro: "Partener valoros, cu o colaborare de lungă durată" } },
  { airline: "LOT Polish Airlines", logo: "lot.webp", w: 140, year: "2019", type: "certificate", doc: "LOT-2019.jpg", title: { en: "Excellent and exemplary cooperation", ro: "Cooperare excelentă și exemplară" } },
  { airline: "Qatar Airways", logo: "qatar.webp", w: 213, year: "2018", type: "certificate", doc: "Qatar.pdf", title: { en: "Sales achievement award", ro: "Premiu pentru rezultatele în vânzări" } },
  { airline: "Hahn Air", logo: "hahn-air.webp", w: 296, year: "2017", type: "certificate", doc: "Hahn Air 2017 thrid place.pdf", title: { en: "3rd top selling agency in Romania", ro: "Locul 3 Top Selling Agency în România" } },
];

const TEXT = {
  en: {
    title: "Recognized by the airlines",
    intro: "Awards and recommendation letters from the airlines we work with every day.",
    types: { trophy: "Trophy", certificate: "Certificate", letter: "Recommendation letter" },
    view: "View document",
  },
  ro: {
    title: "Recunoscuți de companiile aeriene",
    intro: "Premii și scrisori de recomandare de la companiile aeriene cu care lucrăm în fiecare zi.",
    types: { trophy: "Trofeu", certificate: "Certificat", letter: "Scrisoare de recomandare" },
    view: "Vezi documentul",
  },
};

const TYPE_COLOR = { trophy: "orange", certificate: "blue", letter: "green" };

const HomeRecognition = () => {
  const t = useText(TEXT);
  const lang = useLang();

  return (
    <Box py={{ base: 16, md: 24 }}>
      <Container maxW="container.xl">
        <VStack spacing={4} textAlign="center" mb={{ base: 10, md: 14 }}>
          <Heading as="h2" fontFamily="Montserrat" fontWeight="700" fontSize={{ base: "2xl", md: "4xl" }} color="gray.800">
            {t.title}
          </Heading>
          <Text fontSize={{ base: "md", md: "lg" }} color="gray.600" maxW="2xl">
            {t.intro}
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={{ base: 3, md: 5 }}>
          {AWARDS.map((award) => (
            <Link
              key={award.doc}
              href={DOCS + encodeURIComponent(award.doc)}
              isExternal
              data-group
              _hover={{ textDecoration: "none" }}
            >
              <Flex
                direction="column"
                h="full"
                bg="white"
                borderRadius="xl"
                borderWidth="1px"
                borderColor="gray.100"
                boxShadow="0 10px 30px -18px rgba(15, 40, 80, 0.35)"
                p={{ base: 4, md: 6 }}
                transition="all 0.2s"
                _groupHover={{ transform: "translateY(-4px)", boxShadow: "0 18px 40px -18px rgba(15, 40, 80, 0.45)" }}
              >
                <Flex h={{ base: "30px", md: "40px" }} align="center" mb={{ base: 3, md: 4 }}>
                  <Image src={LOGOS + award.logo} alt={`${award.airline} logo`} htmlWidth={award.w} htmlHeight={60} h={{ base: "22px", md: "30px" }} w="auto" maxW="100%" objectFit="contain" loading="lazy" />
                </Flex>
                <Flex gap={2} mb={3} wrap="wrap">
                  <Badge colorScheme={TYPE_COLOR[award.type]} borderRadius="full" px={2.5} py={0.5} textTransform="none" fontSize="xs">
                    {t.types[award.type]}
                  </Badge>
                  <Badge colorScheme="gray" borderRadius="full" px={2.5} py={0.5} fontSize="xs">
                    {award.year}
                  </Badge>
                </Flex>
                <Text as="h3" fontFamily="Montserrat" fontWeight="700" fontSize={{ base: "md", md: "lg" }} color="gray.800" lineHeight="1.35" mb={1}>
                  {award.title[lang]}
                </Text>
                <Text color="gray.600" fontSize="sm" mb={4}>
                  {award.airline}
                </Text>
                <Flex mt="auto" align="center" gap={1.5} color="blue.600" fontWeight="600" fontSize="sm" _groupHover={{ color: "blue.700" }}>
                  <Icon as={FileText} boxSize={4} />
                  {t.view}
                </Flex>
              </Flex>
            </Link>
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default HomeRecognition;
